// full_name: First / Middle / Last / Suffix side by side (stacked on small
// screens). Writes the same object shape as v1 — {first_name, middle_name,
// last_name, suffix} under the field's ref. Saves on blur; the suffix, a
// discrete input, saves on change.

import React from "react";
import { Controller, useFormContext } from "react-hook-form";

import { FieldError, FieldHelper, FieldLabel, inputClass } from "./chrome";
import SelectInput from "./SelectInput";
import { V2FieldProps } from "./types";

interface NamePart {
  key: "first_name" | "middle_name" | "last_name";
  label: string;
  requiredWhenGroupIs: boolean;
  /** Relative width in the row, as a flex weight. */
  weight: number;
}

// The mockup's proportions: first and last widest, middle narrower. Middle
// name skips the "(optional)" marker — convention says it already.
const PARTS: NamePart[] = [
  {
    key: "first_name",
    label: "First name",
    requiredWhenGroupIs: true,
    weight: 3,
  },
  {
    key: "middle_name",
    label: "Middle name",
    requiredWhenGroupIs: false,
    weight: 2,
  },
  {
    key: "last_name",
    label: "Last name",
    requiredWhenGroupIs: true,
    weight: 3,
  },
];

const SUFFIX_OPTIONS = ["Jr.", "Sr.", "I", "II", "III", "IV", "V"].map(
  (suffix) => ({ value: suffix, label: suffix }),
);

export default function FullNameField({ field, onCommit }: V2FieldProps) {
  const { control } = useFormContext();

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 2xs:grid-cols-2 sm:flex">
        {PARTS.map((part) => {
          const name = `${field.ref}.${part.key}`;
          const isRequired = field.required && part.requiredWhenGroupIs;
          return (
            <Controller
              key={part.key}
              control={control}
              name={name}
              rules={{
                validate: (value) => {
                  if (!isRequired) return true;
                  return (
                    String(value ?? "").trim() !== "" ||
                    `${part.label} is required`
                  );
                },
              }}
              render={({ field: rhf, fieldState: { error } }) => (
                <div
                  className="min-w-0"
                  style={{ flex: `${part.weight} 1 0%` }}
                >
                  <FieldLabel text={part.label} htmlFor={name} />
                  <input
                    id={name}
                    name={rhf.name}
                    ref={rhf.ref}
                    type="text"
                    value={rhf.value ?? ""}
                    className={inputClass(Boolean(error))}
                    onChange={rhf.onChange}
                    onBlur={() => {
                      rhf.onBlur();
                      onCommit();
                    }}
                  />
                  <FieldError message={error?.message} />
                </div>
              )}
            />
          );
        })}
        <Controller
          control={control}
          name={`${field.ref}.suffix`}
          render={({ field: rhf }) => (
            <div className="sm:w-28 sm:shrink-0">
              <FieldLabel text="Suffix" htmlFor={`${field.ref}.suffix`} />
              <SelectInput
                id={`${field.ref}.suffix`}
                ariaLabel="Suffix"
                options={SUFFIX_OPTIONS}
                value={typeof rhf.value === "string" ? rhf.value : null}
                placeholder="None"
                clearLabel="None"
                onChange={(value) => {
                  rhf.onChange(value);
                  onCommit();
                }}
                onBlur={rhf.onBlur}
              />
            </div>
          )}
        />
      </div>
      <FieldHelper text={field.description} />
    </div>
  );
}
